/**
 * Convert @ mentions in text to clickable links
 * @param content - The content containing @ mentions
 * @returns HTML string with @ mentions converted to clickable links
 */
import api from '../services/api';

export function convertMentionsToLinks(content: string): string {
  if (!content) return '';

  // Regex to match @mentions (alphanumeric, underscores, and periods)
  const mentionRegex = /@([a-zA-Z0-9_.]+)/g;

  return content.replace(mentionRegex, (match, username) => {
    return `<a href="/profile/${username}" class="mention-link" data-username="${username}">${match}</a>`;
  });
}

/**
 * Handle click events on mention links
 * @param event - The click event
 * @param navigate - React Router navigate function
 */
export async function handleMentionClick(event: React.MouseEvent, navigate: (path: string) => void) {
  const target = event.target as HTMLElement;
  if (!target.classList.contains('mention-link')) return;

  event.preventDefault();
  event.stopPropagation();

  const typedUsername = target.getAttribute('data-username');
  if (!typedUsername) return;

  try {
    // Ask backend to resolve the canonical username (case-insensitive)
    const res = await api.get(`/profile/${encodeURIComponent(typedUsername)}`);
    const canonical = res.data?.user?.username || typedUsername;
    navigate(`/profile/${canonical}`);
  } catch (e) {
    // Fallback: still navigate using typed value
    navigate(`/profile/${typedUsername}`);
  }
}

/**
 * Parse @ mentions from text content
 * @param content - The text content to parse
 * @returns Array of usernames mentioned
 */
export function parseMentions(content: string): string[] {
  if (!content) return [];

  const mentionRegex = /@([a-zA-Z0-9_.]+)/g;
  const mentions: string[] = [];
  let match;

  while ((match = mentionRegex.exec(content)) !== null) {
    mentions.push(match[1]);
  }

  // Remove duplicates without relying on Set iteration (for older TS targets)
  const seen: Record<string, true> = Object.create(null);
  const unique: string[] = [];
  for (let i = 0; i < mentions.length; i++) {
    const m = mentions[i];
    if (!seen[m]) {
      seen[m] = true;
      unique.push(m);
    }
  }
  return unique;
}

